import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'piral-translate';
import { storage, useAction, useGlobalState, usePiletApi } from 'piral-core';
import { PiralNotificationTypes } from 'piral-notifications';
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { useQuickNotesStore } from '../QuickNotes/quickNotes.store';
import { useCalendarDialogStore } from '../CalendarDialog/calendarDialog.store';
import { isChristmasTime } from '../../snowfall-api';
import { FirefliesLevels } from '../../fireflies-api';

// Check if it's summer time (May 1 to September 30)
export function isSummerTime(): boolean {
  const today = new Date();
  const month = today.getMonth(); // 0-indexed: 4 = May, 8 = September
  return month >= 4 && month <= 8;
}
import { useControlCenterStore } from './controlCenter.store';
import { CONTROL_DEFINITIONS } from './controlCenter.defaults';
import { ControlId } from './controlCenter.types';
import { SortableControl } from './SortableControl';
import { ControlRenderer } from './ControlRenderer';

import './control-center.css';

// Icons
const ControlCenterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
    />
  </svg>
);

const BeakerIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
    />
  </svg>
);

interface ControlCenterProps {
  keycloak: any;
}

export const ControlCenter: React.FC<ControlCenterProps> = ({ keycloak }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const translate = useTranslate();
  const { open: openQuickNotes } = useQuickNotesStore();
  const { open: openCalendar } = useCalendarDialogStore();

  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [focusMode, setFocusMode] = useState(() => document.documentElement.classList.contains('focus-mode'));
  const [nightMode, setNightMode] = useState(() => document.documentElement.classList.contains('night-mode'));
  const [maxSnowLevel, setMaxSnowLevel] = useState(5);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [showTestMenu, setShowTestMenu] = useState(false);

  // Control center store for drag/drop
  const { isEditMode, setEditMode, controlOrder, updateOrder, resetOrder } = useControlCenterStore();

  // Pilet API for test functions
  const { showNotification } = usePiletApi();

  // Snowfall state
  const setSnowLevel = useAction('setSnowLevel');
  const setSnowPileEnabled = useAction('setSnowPileEnabled');
  const snowflakeCount = useGlobalState((state: any) => state.snowSettings?.snowflakeCount || 0);
  const snowPileEnabled = useGlobalState((state: any) => state.snowPileEnabled);
  const showSnowfall = isChristmasTime();
  const showFireflies = isSummerTime();

  // Fireflies state
  const setFirefliesLevel = useAction('setFirefliesLevel');
  const firefliesCount = useGlobalState((state: any) => state.firefliesSettings?.count || 0);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Check if user is a developer for extended snow levels and dev features
  useEffect(() => {
    if (!keycloak) return;
    const email = keycloak.idTokenParsed?.['email'];
    const targetUsers = [
      'delstner@sip.de',
      'sinderst@sip.de',
      'rpfahler@sip.de',
      'jblengl@sip.de',
      'mnerlinger@sip.de',
      'drueck@sip.de',
      'fstocker@sip.de',
      'ksabanovic@sip.de',
      'oroell@sip.de',
    ];
    if (targetUsers.includes(email)) {
      setMaxSnowLevel(10);
      setIsDeveloper(true);
    }
  }, [keycloak]);

  // Calculate snow level from snowflake count (count = level * level * 500)
  const getSnowLevelFromCount = (count: number): string => {
    if (count === 0) return '0';
    const level = Math.round(Math.sqrt(count / 500));
    return String(Math.min(Math.max(level, 0), maxSnowLevel));
  };
  const snowLevel = getSnowLevelFromCount(snowflakeCount);

  // Calculate fireflies level from count (count = level * level * 8)
  const getFirefliesLevelFromCount = (count: number): string => {
    if (count === 0) return '0';
    const level = Math.round(Math.sqrt(count / 8));
    return String(Math.min(Math.max(level, 0), 5));
  };
  const firefliesLevel = getFirefliesLevelFromCount(firefliesCount);

  // Sync darkMode, focusMode, and nightMode state with DOM changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(document.documentElement.classList.contains('dark'));
      setFocusMode(document.documentElement.classList.contains('focus-mode'));
      setNightMode(document.documentElement.classList.contains('night-mode'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setEditMode(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setEditMode]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setEditMode(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setEditMode]);

  // Filter visible controls based on conditions
  const visibleControls = controlOrder.filter(id => {
    const def = CONTROL_DEFINITIONS[id];
    if (!def) return false;
    if (def.condition === 'developer' && !isDeveloper) return false;
    if (def.condition === 'snow' && !showSnowfall) return false;
    if (def.condition === 'summer' && !showFireflies) return false;
    return true;
  });

  const handleDragEnd = (event: { active: { id: string | number }; over: { id: string | number } | null }) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const draggedId = active.id as ControlId;
      const overId = over.id as ControlId;

      const oldIndex = controlOrder.indexOf(draggedId);
      const overIndex = controlOrder.indexOf(overId);

      if (oldIndex !== -1 && overIndex !== -1) {
        const newOrder = arrayMove(controlOrder, oldIndex, overIndex);
        updateOrder(newOrder);
      }
    }
  };

  const toggleDarkMode = () => {
    const newTheme = darkMode ? 'light' : 'dark';
    storage.setItem('theme', newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleQuickNotes = () => {
    if (isEditMode) return;
    setIsOpen(false);
    openQuickNotes();
  };

  const handleCalendar = () => {
    if (isEditMode) return;
    setIsOpen(false);
    openCalendar();
  };

  const handleReleaseNotes = () => {
    if (isEditMode) return;
    setIsOpen(false);
    window.dispatchEvent(new CustomEvent('open-release-notes'));
  };

  const handleManageAccount = () => {
    if (isEditMode) return;
    setIsOpen(false);
    keycloak?.accountManagement();
  };

  const toggleFocusMode = () => {
    if (isEditMode) return;
    const newFocusMode = !focusMode;
    setFocusMode(newFocusMode);
    if (newFocusMode) {
      document.documentElement.classList.add('focus-mode');
    } else {
      document.documentElement.classList.remove('focus-mode');
    }
    setIsOpen(false);
  };

  const toggleNightMode = () => {
    if (isEditMode) return;
    const newNightMode = !nightMode;
    setNightMode(newNightMode);
    document.documentElement.classList.toggle('night-mode', newNightMode);
  };

  const toggleTestMenu = () => {
    if (isEditMode) return;
    setShowTestMenu(!showTestMenu);
  };

  // Test functions for developers
  const showBrowserNotification = () => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification('Benachrichtigung', { body: 'Dies ist eine Beispielbenachrichtigung.' });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification('Benachrichtigung', { body: 'Dies ist eine Beispielbenachrichtigung.' });
          }
        });
      }
    }
  };

  const showToast = (type: keyof PiralNotificationTypes) => {
    showNotification('Das ist für den Inhalt gedacht, etwas hat nicht funktioniert oder eben doch.', {
      title: 'Neue Toast Benachrichtigung',
      type,
    });
  };

  const triggerJumpscare = () => {
    setShowTestMenu(false);
    setIsOpen(false);
    window.dispatchEvent(new Event('triggerJumpscare'));
  };

  const controlRendererProps = {
    darkMode,
    focusMode,
    nightMode,
    snowPileEnabled,
    snowLevel,
    maxSnowLevel,
    firefliesLevel,
    showTestMenu,
    currentDay: new Date().getDate(),
    toggleDarkMode: isEditMode ? () => {} : toggleDarkMode,
    handleReleaseNotes,
    handleQuickNotes,
    handleManageAccount,
    toggleFocusMode,
    toggleNightMode: isEditMode ? () => {} : toggleNightMode,
    toggleTestMenu,
    handleCalendar,
    setSnowPileEnabled: isEditMode ? () => {} : setSnowPileEnabled,
    setSnowLevel: isEditMode ? () => {} : setSnowLevel,
    setFirefliesLevel: isEditMode ? () => {} : setFirefliesLevel,
    translate,
  };

  return (
    <div ref={containerRef} className="neo-control-center">
      <button type="button" className="neo-control-center-trigger" onClick={() => setIsOpen(!isOpen)} title={translate('control-center')}>
        <ControlCenterIcon />
      </button>

      {isOpen && (
        <div className="neo-control-center-dropdown">
          <div className={`neo-control-center-panel ${isEditMode ? 'edit-mode' : ''}`}>
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
              <SortableContext items={visibleControls} strategy={rectSortingStrategy}>
                <div className={`neo-control-center-grid ${isEditMode ? 'edit-mode' : ''}`}>
                  {visibleControls.map(id => (
                    <SortableControl key={id} id={id} colSpan={CONTROL_DEFINITIONS[id].colSpan} isEditMode={isEditMode}>
                      <ControlRenderer id={id} {...controlRendererProps} />
                    </SortableControl>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Developer test menu - not draggable, appears when test button is clicked */}
            {isDeveloper && showTestMenu && !isEditMode && (
              <div className="neo-cc-test-menu">
                <div className="neo-cc-divider">
                  <span>Test</span>
                </div>
                <div className="neo-control-center-grid">
                  <button type="button" className="neo-cc-pill" onClick={showBrowserNotification} style={{ gridColumn: 'span 2' }}>
                    <div className="neo-cc-pill-icon">
                      <BeakerIcon />
                    </div>
                    <div className="neo-cc-pill-content">
                      <span className="neo-cc-pill-title">Browser-Notification</span>
                    </div>
                  </button>
                  <button type="button" className="neo-cc-pill" onClick={() => showToast('error')} style={{ gridColumn: 'span 2' }}>
                    <div className="neo-cc-pill-icon">
                      <BeakerIcon />
                    </div>
                    <div className="neo-cc-pill-content">
                      <span className="neo-cc-pill-title">Error-Toast</span>
                    </div>
                  </button>
                  <button type="button" className="neo-cc-pill" onClick={() => showToast('success')} style={{ gridColumn: 'span 2' }}>
                    <div className="neo-cc-pill-icon">
                      <BeakerIcon />
                    </div>
                    <div className="neo-cc-pill-content">
                      <span className="neo-cc-pill-title">Success-Toast</span>
                    </div>
                  </button>
                  <button type="button" className="neo-cc-pill" onClick={triggerJumpscare} style={{ gridColumn: 'span 2' }}>
                    <div className="neo-cc-pill-icon">
                      <BeakerIcon />
                    </div>
                    <div className="neo-cc-pill-content">
                      <span className="neo-cc-pill-title">Nicht anklicken!</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Floating edit pill below the panel */}
          <div className="neo-cc-floating-edit">
            <button
              type="button"
              className={`neo-cc-floating-edit-btn ${isEditMode ? 'active' : ''}`}
              onClick={() => setEditMode(!isEditMode)}
            >
              {isEditMode ? translate('done') : translate('edit')}
            </button>
            {isEditMode && (
              <button type="button" className="neo-cc-floating-edit-btn" onClick={resetOrder}>
                {translate('reset')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
